<template>
  <div class="json-comparison">
    <h1>JSON比对工具</h1>
    
    <!-- 加载状态指示器 -->
    <div class="loading-overlay" v-if="isProcessing">
      <div class="loading-spinner"></div>
      <div class="loading-text">处理中...</div>
    </div>
    
    <!-- 第一个JSON输入区域 -->
    <div class="json-input-section">
      <h2>输入第一个JSON</h2>
      <div class="json-container">
        <div class="json-input-wrapper code-input-wrapper">
          <div class="input-label">输入</div>
          <div class="line-numbers" ref="firstLineNumbers">
            <div
              v-for="(line, idx) in firstJsonLines"
              :key="idx"
              :class="['line-number', { 'error-line-number': firstJsonErrorLine === idx + 1 }]"
            >
              {{ idx + 1 }}
            </div>
          </div>
          <pre class="code-highlight" aria-hidden="true" ref="firstCodeHighlight">
            <div
              v-for="(line, idx) in firstJsonLines"
              :key="idx"
              :class="['code-line', { 'error-line-bg': firstJsonErrorLine === idx + 1 }]"
            >
              {{ line || ' ' }}
            </div>
          </pre>
          <textarea
            v-model="firstJson"
            @input="formatFirstJson"
            placeholder="请输入第一个JSON"
            class="json-input code-input"
            :class="{ 'error': firstJsonError }"
            ref="firstJsonInput"
            @scroll="syncScroll($event, 'first')"
          ></textarea>
          <div class="error-message" v-if="firstJsonError">
            <span class="error-icon">!</span>
            <span class="error-text">{{ firstJsonError }}</span>
            <span class="error-line" v-if="firstJsonErrorLine">第 {{ firstJsonErrorLine }} 行</span>
          </div>
        </div>
        <div class="json-preview-wrapper" v-if="formattedFirstJson">
          <div v-if="firstJsonError" class="preview-error-message">
            <span class="error-icon">!</span>
            <span class="error-text">{{ firstJsonError }}</span>
            <span class="error-line" v-if="firstJsonErrorLine">第 {{ firstJsonErrorLine }} 行</span>
          </div>
          <div class="json-viewer">
            <json-viewer
              v-if="!firstJsonError && parsedFirstJson"
              :value="parsedFirstJson"
              :expand-depth="2"
              :expand-on-click="true"
              :copyable="true"
              :sort="true"
              :show-array-index="true"
              :show-double-quotes="true"
              :show-length="true"
              :show-line="true"
              :show-icon="true"
              :collapsed-on-click-brackets="true"
              :theme="'light'"
            />
            <pre v-else class="json-raw-error">{{ formattedFirstJson }}</pre>
          </div>
        </div>
      </div>
    </div>
    <!-- 第二个JSON输入区域 -->
    <div class="json-input-section" v-if="showSecondInput">
      <h2>输入第二个JSON</h2>
      <div class="json-container">
        <div class="json-input-wrapper code-input-wrapper">
          <div class="input-label">输入</div>
          <div class="line-numbers" ref="secondLineNumbers">
            <div
              v-for="(line, idx) in secondJsonLines"
              :key="idx"
              :class="['line-number', { 'error-line-number': secondJsonErrorLine === idx + 1 }]"
            >
              {{ idx + 1 }}
            </div>
          </div>
          <pre class="code-highlight" aria-hidden="true" ref="secondCodeHighlight">
            <div
              v-for="(line, idx) in secondJsonLines"
              :key="idx"
              :class="['code-line', { 'error-line-bg': secondJsonErrorLine === idx + 1 }]"
            >
              {{ line || ' ' }}
            </div>
          </pre>
          <textarea
            v-model="secondJson"
            @input="formatSecondJson"
            placeholder="请输入第二个JSON"
            class="json-input code-input"
            :class="{ 'error': secondJsonError }"
            ref="secondJsonInput"
            @scroll="syncScroll($event, 'second')"
          ></textarea>
          <div class="error-message" v-if="secondJsonError">
            <span class="error-icon">!</span>
            <span class="error-text">{{ secondJsonError }}</span>
            <span class="error-line" v-if="secondJsonErrorLine">第 {{ secondJsonErrorLine }} 行</span>
          </div>
        </div>
        <div class="json-preview-wrapper" v-if="formattedSecondJson">
          <div v-if="secondJsonError" class="preview-error-message">
            <span class="error-icon">!</span>
            <span class="error-text">{{ secondJsonError }}</span>
            <span class="error-line" v-if="secondJsonErrorLine">第 {{ secondJsonErrorLine }} 行</span>
          </div>
          <div class="json-viewer">
            <json-viewer
              v-if="!secondJsonError && parsedSecondJson"
              :value="parsedSecondJson"
              :expand-depth="2"
              :expand-on-click="true"
              :copyable="true"
              :sort="true"
              :show-array-index="true"
              :show-double-quotes="true"
              :show-length="true"
              :show-line="true"
              :show-icon="true"
              :collapsed-on-click-brackets="true"
              :theme="'light'"
            />
            <pre v-else class="json-raw-error">{{ formattedSecondJson }}</pre>
          </div>
        </div>
      </div>
    </div>
    

    <!-- 比对按钮 -->
    <div class="comparison-actions">
      <button 
        v-if="!showSecondInput" 
        @click="startComparison" 
        class="compare-btn"
        :disabled="!isValidJson(firstJson)"
      >
        输入第二个json
      </button>
      <button 
        v-else 
        @click="compareJsons" 
        class="compare-btn"
        :disabled="!isValidJson(firstJson) || !isValidJson(secondJson)"
      >
        执行比对
      </button>
    </div>

    <!-- 比对结果展示 -->
    <div class="comparison-result" v-if="comparisonResult">
      <h2>比对结果</h2>
      <div class="result-viewer">
        <json-viewer
          :value="comparisonResult"
          :expand-depth="2"
          :expand-on-click="true"
          :copyable="true"
          :sort="true"
          :show-array-index="true"
          :show-double-quotes="true"
          :show-length="true"
          :show-line="true"
          :show-icon="true"
          :collapsed-on-click-brackets="true"
          :theme="'light'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import JsonViewer from 'vue-json-viewer'
import { debounce } from 'lodash-es'

export default {
  name: 'JsonComparison',
  components: {
    JsonViewer
  },
  data() {
    return {
      firstJson: '',
      secondJson: '',
      formattedFirstJson: '',
      formattedSecondJson: '',
      parsedFirstJson: null,
      parsedSecondJson: null,
      showSecondInput: false,
      comparisonResult: null,
      firstJsonError: '',
      secondJsonError: '',
      firstJsonErrorLine: null,
      secondJsonErrorLine: null,
      isProcessing: false
    }
  },
  created() {
    // 使用防抖优化输入处理
    this.debouncedFormatFirstJson = debounce(this.formatFirstJson, 300)
    this.debouncedFormatSecondJson = debounce(this.formatSecondJson, 300)
  },
  computed: {
    firstJsonLines() {
      return this.firstJson.split('\n')
    },
    secondJsonLines() {
      return this.secondJson.split('\n')
    }
  },
  methods: {
    formatFirstJson() {
      if (this.isProcessing) return
      this.isProcessing = true
      
      try {
        // 使用 Web Worker 处理 JSON 解析
        const worker = new Worker(new URL('../workers/jsonWorker.js', import.meta.url))
        
        worker.onmessage = (e) => {
          const { success, data, error } = e.data
          if (success) {
            this.parsedFirstJson = data
            this.formattedFirstJson = this.firstJson
            this.firstJsonError = ''
            this.firstJsonErrorLine = null
          } else {
            this.parsedFirstJson = null
            this.formattedFirstJson = this.firstJson
            this.firstJsonError = this.getErrorMessage(error)
            this.firstJsonErrorLine = this.getErrorLine(error, this.firstJson)
          }
          this.isProcessing = false
          worker.terminate()
        }
        
        worker.postMessage({
          json: this.firstJson,
          action: 'parse'
        })
      } catch (e) {
        this.isProcessing = false
        this.parsedFirstJson = null
        this.formattedFirstJson = this.firstJson
        this.firstJsonError = this.getErrorMessage(e)
        this.firstJsonErrorLine = this.getErrorLine(e, this.firstJson)
      }
    },
    formatSecondJson() {
      if (this.isProcessing) return
      this.isProcessing = true
      
      try {
        const worker = new Worker(new URL('../workers/jsonWorker.js', import.meta.url))
        
        worker.onmessage = (e) => {
          const { success, data, error } = e.data
          if (success) {
            this.parsedSecondJson = data
            this.formattedSecondJson = this.secondJson
            this.secondJsonError = ''
            this.secondJsonErrorLine = null
          } else {
            this.parsedSecondJson = null
            this.formattedSecondJson = this.secondJson
            this.secondJsonError = this.getErrorMessage(error)
            this.secondJsonErrorLine = this.getErrorLine(error, this.secondJson)
          }
          this.isProcessing = false
          worker.terminate()
        }
        
        worker.postMessage({
          json: this.secondJson,
          action: 'parse'
        })
      } catch (e) {
        this.isProcessing = false
        this.parsedSecondJson = null
        this.formattedSecondJson = this.secondJson
        this.secondJsonError = this.getErrorMessage(e)
        this.secondJsonErrorLine = this.getErrorLine(e, this.secondJson)
      }
    },
    getErrorMessage(error) {
      if (!this.firstJson && !this.secondJson) return ''
      
      const message = error.message
      if (message.includes('Unexpected token')) {
        return 'JSON格式错误:存在非法字符'
      } else if (message.includes('Unexpected end')) {
        return 'JSON格式错误:JSON不完整'
      } else if (message.includes('Unexpected number')) {
        return 'JSON格式错误:数字格式不正确'
      } else if (message.includes('Unexpected string')) {
        return 'JSON格式错误:字符串格式不正确'
      } else {
        return 'JSON格式错误:' + message
      }
    },
    getErrorLine(error, json) {
      const message = error.message
      const match = message.match(/position (\d+)/)
      if (match) {
        const position = parseInt(match[1])
        const lines = json.slice(0, position).split('\n')
        return lines.length
      }
      return null
    },
    scrollToError(refName) {
      this.$nextTick(() => {
        const textarea = this.$refs[refName]
        if (textarea) {
          const lineHeight = 24 // 与CSS中的line-height对应
          const errorLine = refName === 'firstJsonInput' ? this.firstJsonErrorLine : this.secondJsonErrorLine
          if (errorLine) {
            const scrollTop = (errorLine - 1) * lineHeight - textarea.clientHeight / 2
            textarea.scrollTop = Math.max(0, scrollTop)
          }
        }
      })
    },
    isValidJson(json) {
      try {
        JSON.parse(json)
        return true
      } catch (e) {
        return false
      }
    },
    startComparison() {
      if (this.isValidJson(this.firstJson)) {
        this.showSecondInput = true
      }
    },
    compareJsons() {
      if (!this.isValidJson(this.firstJson) || !this.isValidJson(this.secondJson)) {
        return
      }

      this.isProcessing = true
      
      try {
        const worker = new Worker(new URL('../workers/jsonWorker.js', import.meta.url))
        
        worker.onmessage = (e) => {
          const { success, data } = e.data
          if (success) {
            if (Object.keys(data).length === 0) {
              this.comparisonResult = "完全一致"
            } else {
              this.comparisonResult = data
            }
          }
          this.isProcessing = false
          worker.terminate()
        }
        
        worker.postMessage({
          json1: this.firstJson,
          json2: this.secondJson,
          action: 'compare'
        })
      } catch (e) {
        this.isProcessing = false
        console.error('比对过程出错:', e)
      }
    },
    syncScroll(e, which) {
      // 让pre和textarea和行号同步滚动
      const wrapper = which === 'first' ? this.$el.querySelector('.code-input-wrapper') : null
      if (wrapper) {
        const pre = wrapper.querySelector('.code-highlight')
        pre.scrollTop = e.target.scrollTop
        pre.scrollLeft = e.target.scrollLeft
        const lineNumbers = this.$refs.firstLineNumbers
        if (lineNumbers) {
          lineNumbers.scrollTop = e.target.scrollTop
        }
      }
    }
  }
}
</script>

<style scoped>
.json-comparison {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  background: #ffffff;
  min-height: 100vh;
}

h1 {
  color: #2c3e50;
  margin-bottom: 2rem;
  font-size: 2rem;
  text-align: center;
  font-weight: 600;
}

h2 {
  color: #34495e;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

h2::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 1.25rem;
  background: #4CAF50;
  border-radius: 2px;
}

.json-input-section {
  margin-bottom: 2rem;
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 1px solid #eef2f7;
}

.json-input-section:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  border-color: #e0e7ff;
}

.json-container {
  display: flex;
  gap: 2rem;
  margin-top: 1rem;
  position: relative;
  flex-wrap: nowrap;
}

.json-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 1px;
  height: 100%;
  background: linear-gradient(to bottom, transparent, #e0e7ff, transparent);
  transform: translateX(-50%);
}

.json-input-wrapper.code-input-wrapper,
.json-preview-wrapper {
  width: 50%;
  flex: none;
  min-width: 0;
  max-width: 50%;
  box-sizing: border-box;
}

.json-preview-wrapper {
  min-width: 0;
  height: 500px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.03);
  padding: 1.5rem 1rem 1rem;
  margin: 0;
  overflow: auto;
}

.preview-error-message {
  flex-shrink: 0;
}

.json-viewer, .json-raw-error {
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
}

.input-label {
  position: absolute;
  top: -0.5rem;
  left: 1rem;
  background: #4CAF50;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  z-index: 10;
  pointer-events: none;
}

.json-input {
  width: 100%;
  height: 500px;
  padding: 1.5rem 1rem 1rem;
  border: 2px solid #e0e7ff;
  border-radius: 8px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 24px;
  transition: all 0.3s ease;
  resize: none;
  background: #fafbff;
  color: #2c3e50;
  tab-size: 2;
}

.json-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.1);
  background: #ffffff;
}

.json-viewer {
  height: 500px;
  background: #fafbff;
  border: 2px solid #e0e7ff;
  border-radius: 8px;
  padding: 1.5rem 1rem 1rem;
  overflow: auto;
}

/* 自定义滚动条样式 */
.json-viewer::-webkit-scrollbar,
.json-input::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.json-viewer::-webkit-scrollbar-track,
.json-input::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.json-viewer::-webkit-scrollbar-thumb,
.json-input::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.json-viewer::-webkit-scrollbar-thumb:hover,
.json-input::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 自定义JSON查看器样式 */
:deep(.jv-container) {
  background: transparent;
  border-radius: 6px;
  padding: 0;
  text-align: left;
}

:deep(.jv-container .jv-code) {
  text-align: left;
}

:deep(.jv-container .jv-item) {
  text-align: left;
}

:deep(.jv-container .jv-ellipsis) {
  text-align: left;
}

:deep(.jv-container .jv-array) {
  text-align: left;
}

:deep(.jv-container .jv-object) {
  text-align: left;
}

:deep(.jv-container .jv-property) {
  text-align: left;
}

:deep(.jv-container .jv-key) {
  color: #881391;
  font-weight: 500;
  text-align: left;
}

:deep(.jv-container .jv-string) {
  color: #c41a16;
  text-align: left;
}

:deep(.jv-container .jv-number) {
  color: #1c00cf;
  font-weight: 500;
  text-align: left;
}

:deep(.jv-container .jv-boolean) {
  color: #0000ff;
  font-weight: 500;
  text-align: left;
}

:deep(.jv-container .jv-null) {
  color: #808080;
  font-weight: 500;
  text-align: left;
}

.comparison-actions {
  margin: 2rem 0;
  text-align: center;
}

.compare-btn {
  background-color: #4CAF50;
  color: white;
  padding: 0.875rem 2.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2);
}

.compare-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  box-shadow: none;
}

.compare-btn:hover:not(:disabled) {
  background-color: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.3);
}

.comparison-result {
  margin-top: 2rem;
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #eef2f7;
}

.result-viewer {
  background: #fafbff;
  padding: 1.5rem;
  border-radius: 8px;
  border: 2px solid #e0e7ff;
  margin-top: 1rem;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .json-comparison {
    padding: 0.25rem;
    min-width: 0;
  }
  h1 {
    font-size: 1rem;
    margin-bottom: 1rem;
    padding: 0 0.25rem;
    text-align: left;
  }
  h2 {
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
    padding-left: 0.25rem;
  }
  .json-input-section {
    padding: 0.25rem 0.25rem 0.5rem 0.25rem;
    margin-bottom: 1rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  }
  .json-container {
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
  .json-input-wrapper.code-input-wrapper,
  .json-preview-wrapper {
    width: 100%;
    max-width: 100%;
  }
  .line-numbers {
    width: 22px;
    font-size: 10px;
    padding: 0.5rem 0 0.25rem 0;
    border-radius: 8px 0 0 8px;
    background: #f3f4f6;
  }
  .line-number {
    height: 18px;
    padding-right: 4px;
  }
  .code-highlight {
    left: 22px;
    width: calc(100% - 22px);
    font-size: 10px;
    padding: 0.5rem 0.25rem 0.25rem;
    border-radius: 0 8px 8px 0;
  }
  .code-input {
    left: 22px;
    width: calc(100% - 22px);
    font-size: 10px;
    padding: 0.5rem 0.25rem 0.25rem;
    border-radius: 0 8px 8px 0;
    height: 100%;
  }
  .input-label {
    left: 0.25rem;
    top: -0.5rem;
    font-size: 0.6rem;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
  }
  .json-preview-wrapper {
    min-width: 0;
    height: 180px;
    margin-top: 0.25rem;
    padding: 0.25rem;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.03);
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: auto;
  }
  .json-viewer, .json-raw-error {
    flex: 1 1 0;
    min-height: 0;
    overflow: auto;
  }
  .error-message {
    font-size: 0.7rem;
    padding: 0.3rem;
    margin-top: 0.15rem;
    border-radius: 4px;
  }
  .comparison-result {
    padding: 0.5rem;
    border-radius: 8px;
    margin-top: 1rem;
  }
  .result-viewer {
    padding: 0.25rem;
    font-size: 10px;
    border-radius: 8px;
  }
  .compare-btn {
    width: 100%;
    padding: 0.4rem 0.4rem;
    font-size: 0.9rem;
    border-radius: 6px;
    margin-top: 0.5rem;
  }
}

.json-input.error {
  border-color: #ff4d4f;
  background-color: #fff2f0;
}

.json-input.error:focus {
  box-shadow: 0 0 0 4px rgba(255, 77, 79, 0.1);
}

.error-line-marker {
  position: absolute;
  left: 0;
  width: 4px;
  height: 24px;
  background-color: #ff4d4f;
  z-index: 2;
}

.error-message {
  color: #ff4d4f;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
  background-color: #fff2f0;
  border-radius: 4px;
  border: 1px solid #ffccc7;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: #ff4d4f;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
}

.error-text {
  flex: 1;
}

.error-line {
  background-color: #ff4d4f;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.code-input-wrapper {
  position: relative;
  width: 100%;
  height: 500px;
  display: flex;
  flex-direction: row;
}
.line-numbers {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  padding: 1.5rem 0 1rem 0;
  background: #f3f4f6;
  border-radius: 8px 0 0 8px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 24px;
  color: #bdbdbd;
  text-align: right;
  z-index: 3;
  user-select: none;
  overflow: hidden;
}
.line-number {
  height: 24px;
  padding-right: 8px;
  box-sizing: border-box;
}
.error-line-number {
  color: #ff4d4f;
  font-weight: bold;
  background: #fff1f0;
  border-radius: 4px;
}
.code-highlight {
  position: absolute;
  left: 40px; top: 0; right: 0; bottom: 0;
  width: calc(100% - 40px);
  height: 91%;
  margin: 0;
  padding: 1.5rem 1rem 1rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 24px;
  background: #fafbff;
  color: transparent;
  z-index: 1;
  pointer-events: none;
  overflow: auto;
  border-radius: 0 8px 8px 0;
}
.code-input {
  position: absolute;
  left: 40px; top: 0; right: 0; bottom: 0;
  width: calc(100% - 40px);
  height: 91%;
  padding: 1.5rem 1rem 1rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 24px;
  background: transparent;
  color: #2c3e50;
  border: 2px solid #e0e7ff;
  border-radius: 0 8px 8px 0;
  resize: none;
  z-index: 2;
  outline: none;
  box-shadow: none;
}
.code-input.error {
  border-color: #ff4d4f;
}
.code-input:focus {
  background: transparent;
}

.preview-error-message {
  color: #ff4d4f;
  font-size: 0.95em;
  margin-bottom: 0.5em;
  padding: 0.5em 0.75em;
  background: #fff2f0;
  border-radius: 4px;
  border: 1px solid #ffccc7;
  display: flex;
  align-items: center;
  gap: 0.5em;
}
.json-raw-error {
  color: #ff4d4f;
  background: #fff2f0;
  border-radius: 4px;
  padding: 1em;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-all;
  min-height: 80px;
  margin: 0;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 1rem;
  color: #4CAF50;
  font-size: 1rem;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>